import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog } from '@fortawesome/free-solid-svg-icons';
import { CircleUserRound } from 'lucide-react';
import { Icon24, IconSpecial } from './ui';
import './Sidebar.scss';

const variantClassMap = {
  default: 'app-sidebar--default common-sidebar',
  b2b: 'app-sidebar--b2b b2b-sidebar',
  superAdmin: 'app-sidebar--super-admin sa-sidebar',
};

function SidebarIcon({ item, active, hovered }) {
  if (item.sidebarIcon) {
    const SidebarSvgIcon = item.sidebarIcon;
    return <SidebarSvgIcon className="app-sidebar__nav-icon" aria-hidden="true" />;
  }

  if (item.lucideIcon) {
    const LucideIcon = item.lucideIcon;
    return <LucideIcon className="app-sidebar__lucide-icon" aria-hidden="true" />;
  }

  if (item.faIcon) {
    return <FontAwesomeIcon icon={item.faIcon} />;
  }

  const iconName = active || hovered ? item.iconNameActive || item.iconName : item.iconName;
  return <Icon24 name={iconName} />;
}

function ActionIcon({ action }) {
  if (action.lucideIcon) {
    const LucideIcon = action.lucideIcon;
    return <LucideIcon className="app-sidebar__settings-lucide-icon" aria-hidden="true" />;
  }

  return <FontAwesomeIcon icon={action.icon || faCog} />;
}

function SidebarOpenIcon() {
  return (
    <span className="app-sidebar__open-icon" aria-hidden="true">
      <span className="app-sidebar__open-icon-surface" />
      <span className="app-sidebar__open-icon-panel" />
      <span className="app-sidebar__open-icon-arrow" />
    </span>
  );
}

function SidebarNavLink({ item, className, children, onClick }) {
  if (item.external) {
    return (
      <a
        href={item.path}
        className={className}
        onMouseEnter={item.onMouseEnter}
        onMouseLeave={item.onMouseLeave}
        onClick={onClick}
      >
        {children}
      </a>
    );
  }

  return (
    <Link
      to={item.path}
      className={className}
      onMouseEnter={item.onMouseEnter}
      onMouseLeave={item.onMouseLeave}
      onClick={onClick}
    >
      {children}
    </Link>
  );
}

function SidebarSettingsMenuLink({ item, active }) {
  const className = `app-sidebar__settings-menu-item ${active ? 'active' : ''}`;

  if (item.external) {
    return (
      <a href={item.path} className={className} onClick={item.onClick}>
        {item.label}
      </a>
    );
  }

  return (
    <Link to={item.path} className={className} onClick={item.onClick}>
      {item.label}
    </Link>
  );
}

function AppSidebar({
  variant = 'default',
  isCollapsed = false,
  settingsMode = false,
  onToggleCollapse,
  onLogoClick,
  navItems,
  navSectionLabel,
  settingsAction,
  settingsPanel,
  settingsPanelTitle = '설정',
  settingsItems = [],
  isSettingsItemActive,
  hideToggleButton = false,
  userName,
  userEmail,
  profileSubtitle,
  profileMenuItems = [],
}) {
  const [isLogoHovered, setIsLogoHovered] = useState(false);
  const [hoveredNavItem, setHoveredNavItem] = useState(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  const rootClasses = [
    'app-sidebar',
    variantClassMap[variant],
    isCollapsed ? 'collapsed app-sidebar--collapsed' : '',
    settingsMode ? 'settings-mode app-sidebar--settings-mode' : '',
  ].filter(Boolean).join(' ');
  const canShowProfileText = !isCollapsed && (userName || userEmail);
  const canShowCollapsedProfileIcon = variant === 'b2b' && isCollapsed && (userName || userEmail);
  const canShowProfileMenu = (canShowProfileText || canShowCollapsedProfileIcon)
    && showProfileMenu
    && profileMenuItems.length > 0;
  const hasProfileArea = settingsAction || canShowProfileText || canShowCollapsedProfileIcon;
  const canToggleViaLogo = Boolean(onLogoClick || (isCollapsed && onToggleCollapse));
  const brandAriaLabel = isCollapsed ? '사이드바 펼치기' : undefined;
  const renderedSettingsPanel = settingsPanel || (
    settingsItems.length > 0 ? (
      <div className="app-sidebar__settings-panel">
        <div className="app-sidebar__settings-panel-title">{settingsPanelTitle}</div>
        <nav className="app-sidebar__settings-menu">
          {settingsItems.map((item) => (
            <SidebarSettingsMenuLink
              key={item.key}
              item={item}
              active={isSettingsItemActive ? isSettingsItemActive(item) : Boolean(item.active)}
            />
          ))}
        </nav>
      </div>
    ) : null
  );

  const handleLogoClick = () => {
    if (onLogoClick) {
      onLogoClick();
      return;
    }

    if (isCollapsed && onToggleCollapse) {
      onToggleCollapse();
    }
  };

  const renderCollapsedLogo = () => (
    isLogoHovered ? (
      <SidebarOpenIcon />
    ) : variant === 'b2b' ? (
      <img
        src="/reops_symbol.svg"
        alt="ReOpsAI"
        className="app-sidebar__logo app-sidebar__logo--symbol"
      />
    ) : (
      <IconSpecial name="symbol" className="app-sidebar__logo" />
    )
  );

  const logo =
    variant === 'b2b' ? (
      isCollapsed ? (
        renderCollapsedLogo()
      ) : (
        <IconSpecial name="logo" className="app-sidebar__logo app-sidebar__logo--full" />
      )
    ) : isCollapsed ? (
      renderCollapsedLogo()
    ) : (
      <IconSpecial name="logo" className="app-sidebar__logo" />
    );

  const renderProfileMenu = () => (
    <div
      className={`app-sidebar__profile-menu sa-profile-menu ${
        isCollapsed ? 'app-sidebar__profile-menu--collapsed' : ''
      }`}
    >
      {profileMenuItems.map((item) => (
        <button
          key={item.key}
          type="button"
          className={`app-sidebar__profile-menu-item sa-profile-menu-item ${item.danger ? 'danger' : ''}`}
          onClick={() => {
            setShowProfileMenu(false);
            item.onClick();
          }}
          title={item.title || item.label}
        >
          {item.icon ? <FontAwesomeIcon icon={item.icon} /> : null}
          <span>{item.label}</span>
        </button>
      ))}
    </div>
  );

  return (
    <aside className={rootClasses}>
      <div className="app-sidebar__header">
        <button
          type="button"
          className="app-sidebar__brand"
          onMouseEnter={() => isCollapsed && setIsLogoHovered(true)}
          onMouseLeave={() => setIsLogoHovered(false)}
          onClick={canToggleViaLogo ? handleLogoClick : undefined}
          disabled={!canToggleViaLogo}
          aria-label={brandAriaLabel}
          title={brandAriaLabel}
        >
          {logo}
        </button>

        {!hideToggleButton && !isCollapsed && !settingsMode && onToggleCollapse ? (
          <button
            type="button"
            className="app-sidebar__toggle"
            onClick={onToggleCollapse}
            title="사이드바 접기"
            aria-label="사이드바 접기"
          >
            <IconSpecial name="sidebarFolding" />
          </button>
        ) : null}
      </div>

      <nav className="app-sidebar__nav">
        {navSectionLabel ? <div className="app-sidebar__nav-section">{navSectionLabel}</div> : null}
        {navItems.map((item) => {
          const active = Boolean(item.active);
          const hovered = hoveredNavItem === item.key;
          const children = Array.isArray(item.children) ? item.children : [];
          const hasChildren = children.length > 0;
          const itemWithHover = {
            ...item,
            onMouseEnter: () => setHoveredNavItem(item.key),
            onMouseLeave: () => setHoveredNavItem(null),
          };

          return (
            <div
              key={item.key}
              className={`app-sidebar__nav-group ${active || children.some((child) => child.active) ? 'is-open' : ''}`}
            >
              <SidebarNavLink
                item={itemWithHover}
                className={`app-sidebar__nav-item ${active ? 'active' : ''}`}
                onClick={item.onClick}
              >
                <SidebarIcon item={item} active={active} hovered={hovered} />
                <span className="app-sidebar__nav-label">{item.label}</span>
                <span className="app-sidebar__tooltip">{item.label}</span>
              </SidebarNavLink>
              {hasChildren && !isCollapsed ? (
                <div className="app-sidebar__nav-children">
                  {children.map((child) => (
                    <SidebarNavLink
                      key={child.key}
                      item={child}
                      className={`app-sidebar__nav-child ${child.active ? 'active' : ''}`}
                      onClick={child.onClick}
                    >
                      <span className="app-sidebar__nav-label">{child.label}</span>
                    </SidebarNavLink>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </nav>

      {hasProfileArea ? (
        <div className="app-sidebar__profile">
          {settingsAction ? (
            <button
              type="button"
              className={`app-sidebar__settings-button b2b-settings-icon-button ${settingsAction.active ? 'active' : ''}`}
              onClick={settingsAction.onClick}
              title={settingsAction.title || settingsAction.label}
            >
              <ActionIcon action={settingsAction} />
              <span>{settingsAction.label}</span>
            </button>
          ) : null}

          {canShowProfileText ? (
            <div className="app-sidebar__profile-expanded app-sidebar__profile-anchor">
              <button
                type="button"
                className="app-sidebar__profile-info"
                onClick={() => setShowProfileMenu((prev) => !prev)}
                title={userName || '사용자'}
                aria-expanded={showProfileMenu}
              >
                {variant === 'b2b' ? (
                  <CircleUserRound className="app-sidebar__profile-avatar" aria-hidden="true" />
                ) : null}
                <div className="app-sidebar__profile-details">
                  <div className="app-sidebar__profile-name">{userName || '사용자'}</div>
                  <div className="app-sidebar__profile-email">
                    {userEmail || profileSubtitle || 'user@example.com'}
                  </div>
                </div>
              </button>
              {canShowProfileMenu ? renderProfileMenu() : null}
            </div>
          ) : null}

          {canShowCollapsedProfileIcon ? (
            <div className="app-sidebar__profile-anchor app-sidebar__profile-collapsed-wrap">
              <button
                type="button"
                className="app-sidebar__profile-collapsed"
                onClick={() => setShowProfileMenu((prev) => !prev)}
                title={userName || '사용자'}
                aria-label={`${userName || '사용자'} 메뉴`}
                aria-expanded={showProfileMenu}
              >
                <CircleUserRound className="app-sidebar__profile-avatar" aria-hidden="true" />
              </button>
              {canShowProfileMenu ? renderProfileMenu() : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {settingsMode ? renderedSettingsPanel : null}
    </aside>
  );
}

export default AppSidebar;
